import { ChecklistItemNoteRecord } from "models/view-models/checklist-item-notes/checklist-item-note-record";
import { useState } from "react";
import { useReadonlyContext } from "utilities/contexts/readonly/use-readonly-context";
import useQuillImageTagSanitizer from "utilities/hooks/quill/use-quill-image-tag-sanitizer";
import ChecklistItemNoteService, {
    ChecklistItemNoteCreateParams,
    ChecklistItemNoteResourcePathParams,
    ChecklistItemNoteUpdateParams,
} from "utilities/services/checklist-item-notes/checklist-item-note-service";

interface UseChecklistItemNote {
    createChecklistItemNote: (
        checklistItemId: number,
        text: string
    ) => Promise<ChecklistItemNoteRecord | undefined>;
    deleteChecklistItemNote: (
        checklistItemNote?: ChecklistItemNoteRecord
    ) => Promise<void>;
    isSaving: boolean;
    updateChecklistItemNote: (
        checklistItemNote: ChecklistItemNoteRecord
    ) => Promise<ChecklistItemNoteRecord | undefined>;
}

const useChecklistItemNote = (): UseChecklistItemNote => {
    const [isSaving, setIsSaving] = useState(false);
    const { create: createChecklistItemNoteApi } =
        ChecklistItemNoteService.useCreate();
    const { delete: deleteChecklistItemNoteApi } =
        ChecklistItemNoteService.useDelete();
    const { update: updateChecklistItemNoteApi } =
        ChecklistItemNoteService.useUpdate();
    const { skipIfIsReadonly } = useReadonlyContext();
    const { sanitizeImageRichTextContent } = useQuillImageTagSanitizer();

    const createChecklistItemNote = async (
        checklistItemId: number,
        text: string
    ): Promise<ChecklistItemNoteRecord | undefined> => {
        setIsSaving(true);

        try {
            const requestData: ChecklistItemNoteCreateParams = {
                checklistItemId: checklistItemId,
                text: text,
            };

            requestData.text = sanitizeImageRichTextContent(requestData.text);

            const response = await createChecklistItemNoteApi(requestData);

            if (response?.result == null || response.resultObject == null) {
                throw new Error(
                    `No result returned when creating checklist item note for checklist item with ID ${checklistItemId}`
                );
            }

            if (response.result.hasErrors()) {
                throw new Error(response.result.listErrorMessages().join(", "));
            }

            return response.resultObject;
        } catch (error) {
            console.error(error);

            return undefined;
        } finally {
            setIsSaving(false);
        }
    };

    const deleteChecklistItemNote = skipIfIsReadonly(
        async (checklistItemNote?: ChecklistItemNoteRecord) => {
            if (!checklistItemNote?.id) {
                throw new Error("Checklist item note ID is not valid");
            }

            setIsSaving(true);

            try {
                const deleteResponse = await deleteChecklistItemNoteApi(
                    checklistItemNote.id,
                    {
                        id: checklistItemNote.id,
                    }
                );

                if (
                    deleteResponse?.result == null ||
                    deleteResponse.resultObject == null
                ) {
                    throw new Error(
                        `No result when deleting a checklist item note with ID ${checklistItemNote.id}`
                    );
                }
                if (deleteResponse.result?.hasErrors()) {
                    throw new Error(
                        deleteResponse.result.listErrorMessages().join(",")
                    );
                }
            } catch (error) {
                throw error;
            } finally {
                setIsSaving(false);
            }
        }
    );

    const updateChecklistItemNote = async (
        checklistItemNote: ChecklistItemNoteRecord
    ): Promise<ChecklistItemNoteRecord | undefined> => {
        if (checklistItemNote?.id == null || checklistItemNote.id <= 0) {
            return;
        }

        setIsSaving(true);

        try {
            const requestData: ChecklistItemNoteUpdateParams = {
                text: checklistItemNote.text,
            };
            const pathParams: ChecklistItemNoteResourcePathParams = {
                id: checklistItemNote.id,
            };

            requestData.text = sanitizeImageRichTextContent(requestData.text);

            const response = await updateChecklistItemNoteApi(
                requestData,
                pathParams
            );

            if (response?.result == null || response.resultObject == null) {
                throw new Error(
                    `No result returned when creating checklist item note for checklist item with ID ${checklistItemNote.id}`
                );
            }

            if (response.result.hasErrors()) {
                throw new Error(response.result.listErrorMessages().join(", "));
            }

            return response.resultObject;
        } catch (error) {
            console.error(error);

            return undefined;
        } finally {
            setIsSaving(false);
        }
    };

    return {
        createChecklistItemNote: createChecklistItemNote,
        deleteChecklistItemNote: deleteChecklistItemNote,
        isSaving: isSaving,
        updateChecklistItemNote: updateChecklistItemNote,
    };
};

export default useChecklistItemNote;
